import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      default: 'size-10', // 40px
      xs: 'size-6', // 24px
      sm: 'size-8', // 32px
      md: 'size-16', // 64px
      lg: 'size-32 [&_span]:text-3xl', // 128px
      xl: 'size-40 [&_span]:text-3xl' // 160px
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const Avatar = React.forwardRef(({ size, className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(avatarVariants({ size }), className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn('flex h-full w-full cursor-default items-center justify-center rounded-full bg-primary-lightest text-primary-dark', className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const AvatarWrapper = React.forwardRef(({ className, src, text, size, ...props }, ref) => {
  function getInitialsFromFullName(fullName) {
    if (!fullName) {
      return '';
    }

    const nameParts = fullName.split(' ').filter(word => word.length > 0);

    if (nameParts.length === 0) {
      return '';
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();

    if (nameParts.length > 1) {
      const secondInitial = nameParts[1].charAt(0).toUpperCase();
      return firstInitial + secondInitial;
    }

    return firstInitial;
  }

  return (
    <Avatar
      ref={ref}
      size={size}
      className={cn('c-avatar-wrapper', className)}
      {...props}
    >
      <AvatarImage
        src={src}
        alt={text}
      />
      <AvatarFallback>{getInitialsFromFullName(text)}</AvatarFallback>
    </Avatar>
  );
});
AvatarWrapper.displayName = 'AvatarWrapper';

export { Avatar, AvatarImage, AvatarFallback, AvatarWrapper };
